import { HttpService } from '@/shared/api/services/HttpService';
import { buildUrlForServiceName } from '@/connection/url-retriever';
import conf from '@/shared/config/useConfig';
import type {
  BuergschaftDetailsFuerAufgabenListe,
  Bürgschaft as Buergschaft,
  BürgschaftHistorie as BuergschaftHistorie,
} from '@/types/Bürgschaft';
import type {
  AbgetreteBuergschaftListResponse,
  BürgschaftFilter as BuergschaftFilter,
  BürgschaftFilterResponse as BuergschaftFilterResponse,
} from '@/connection/filter-helper/filter-helper-bürgschaften';
import { configForDownload, configForJsonPostDownload } from '@/connection/base';
import type { BürgschaftstemplateAllgemein as BuergschaftstemplateAllgemein } from '@/types/Dokument';
import type { FilterOptionen } from '@/components/datatable/types/FilterObject.ts';

export class SharedBuergschaftService extends HttpService {
  protected buergschaftServiceBaseUrl = `${buildUrlForServiceName(
    conf.bürgschaftserviceName,
  )}/buergschaftservice/api/private/v2`;

  protected projektServiceBaseUrl = `${buildUrlForServiceName(
    conf.bürgschaftserviceName,
  )}/buergschaftservice/api/private/v1`;

  async holeBuergschaftDetails(id: number): Promise<Buergschaft> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaften/${id}`);
  }

  async holeBuergschaftHistorie(id: string): Promise<BuergschaftHistorie[]> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaft/${id}/historie`);
  }

  async holeAlleBuergschaftenCsv(buergschaftFilter: BuergschaftFilter): Promise<BlobPart> {
    /**
     * AG: Die Aufruferrolle wird in der Domäne AG mitgegeben, um zu unterscheiden, ob die
     * ausgestellten oder eingegangene Bürgschaften exportiert werden sollen
     * TL, BÜ: in den anderen beiden Domänen werden immer alle Bürgschaften exportiert
     */
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/filterung/buergschaften.csv`,
      {
        data: buergschaftFilter,
        config: configForJsonPostDownload,
      },
    );
  }

  async holeBuergschaftCsv(buergschaftId: number | string): Promise<BlobPart> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${buergschaftId}/buergschaften.csv`,
      { config: configForDownload },
    );
  }

  async findeTemplatesNachId(id: string | number): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(`${this.buergschaftServiceBaseUrl}/buergschaftstemplates/${id}`);
  }

  async holeFilterOptionenBuergschaften(
    buergschaftFilter: BuergschaftFilter,
  ): Promise<FilterOptionen> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/filteroptionen`, {
      data: {
        aufruferRolle: buergschaftFilter?.aufruferRolle,
        buergschaftIdsIn: buergschaftFilter?.idsIn,
        buergschaftIdsNotIn: buergschaftFilter?.idsNotIn,
        mutterUnternehmenId: buergschaftFilter?.mutterUnternehmenId,
        kautionsvertragIds: buergschaftFilter?.kautionsvertragIds,
        buergeIds: buergschaftFilter?.bürgen,
      },
    });
  }

  async holeBuergschaftDetailsFuerAufgabenListe(
    id: string,
  ): Promise<BuergschaftDetailsFuerAufgabenListe> {
    return this.httpClient.get(
      `${this.buergschaftServiceBaseUrl}/buergschaften/${id}/aufgabendetails`,
    );
  }

  async holeBuergschaftenMitPaginierungUndFilterung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    buergschaftFilter: BuergschaftFilter,
  ): Promise<BuergschaftFilterResponse> {
    return this.httpClient.post(`${this.buergschaftServiceBaseUrl}/buergschaften/filterung`, {
      params: { page: seite, size: seitengroesse, sort: `${property},${sortierArt}` },
      data: buergschaftFilter,
    });
  }

  async holeAbgetreteneBuergschaften(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    buergschaftFilter?: BuergschaftFilter,
  ): Promise<AbgetreteBuergschaftListResponse> {
    return this.httpClient.post(
      `${this.buergschaftServiceBaseUrl}/buergschaften/abgetreten/liste`,
      {
        params: { page: seite, size: seitengroesse, sort: `${property},${sortierArt}` },
        data: buergschaftFilter,
      },
    );
  }
}
